import * as React from 'react';
import { Sheet, SheetContent, SheetClose, SheetTitle, SheetDescription } from '@shelflix/ui-web/components/ui/sheet';
import { cn } from '@/utils/utils';
import { useDrawerSwipe } from '@/hooks/useDrawerSwipe';
import Icon from '@shelflix/ui-web/components/Icon';

interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: string;
  description?: string;
  width?: string; // Custom width (e.g., "30%", "400px")
  className?: string;
}

export function Drawer({
  open,
  onOpenChange,
  children,
  title = 'Drawer',
  description = 'Drawer content',
  width,
  className,
}: DrawerProps) {
  // Default widths: 80% mobile, 30% desktop
  const defaultWidth = width || 'w-[80%] md:w-[30%]';

  // Enable swipe to close on mobile
  const swipeRef = useDrawerSwipe({
    onClose: () => onOpenChange(false),
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideCloseButton={true}
        className={cn(
          '!bg-background border-l border-neutral-600 p-0',
          defaultWidth,
          '!max-w-none', // Remove default max-w-sm constraint
          className
        )}
      >
        {/* Hidden title and description for accessibility */}
        <SheetTitle className="sr-only">{title}</SheetTitle>
        <SheetDescription className="sr-only">{description}</SheetDescription>

        <div ref={swipeRef} className="relative h-full">
          {/* Invisible header div with close button */}
          <div className="invisible relative h-0">
            <SheetClose className="visible absolute top-4 right-4 z-50 cursor-pointer opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
              <Icon name="close" size={20} />
              <span className="sr-only">Close</span>
            </SheetClose>
          </div>

          {/* Content - overflow-x-hidden prevents horizontal scroll */}
          <div className="flex h-full flex-col overflow-x-hidden overflow-y-auto p-6 pt-14">{children}</div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
